// Path A — Department-wise sidebar realignment.
// Routes/keys are UNCHANGED. Only grouping, ordering, and visibility differ.
// Permissions are NOT wired live (Phase B does that). Visibility stays config-driven.

import {
  ChartBar, Users, Megaphone, Package, Calendar, Envelope, ChartLine,
  AddressBook, Handshake, ListChecks, ClockCounterClockwise, DownloadSimple,
  TrendUp, Trophy, Phone, Funnel, ShareNetwork, UserPlus, Sparkle, WhatsappLogo,
  Receipt, Gift, Note, Bell, ChatTeardropText, Question, Kanban, Gear, Buildings,
  ShoppingCart, Truck, Wrench, IdentificationCard, FileText, Tag, CurrencyInr, Pulse,
} from "@phosphor-icons/react";

// ============================================================================
// DEPARTMENT ITEM POOLS — each role picks from these by `key`.
// All paths exist as routes today. Items not yet built are intentionally absent.
// ============================================================================

// A. DASHBOARDS
export const ALL_DASHBOARD_ITEMS = [
  { path: '/dashboard',                     key: 'dashboard',                  label: 'Dashboard',                    icon: ChartBar,    testId: 'nav-dashboard' },
  { path: '/my-dashboard',                  key: 'my-dashboard',               label: 'My Dashboard',                 icon: TrendUp,     testId: 'nav-my-dashboard' },
  { path: '/manager-dashboard',             key: 'manager-dashboard',          label: 'Manager Dashboard',            icon: ChartLine,   testId: 'nav-manager-dashboard' },
  { path: '/bde-dashboard',                 key: 'bde-dashboard',              label: 'BDE Dashboard',                icon: Trophy,      testId: 'nav-bde-dashboard' },
  { path: '/dashboard/md',                  key: 'dashboard/md',               label: 'MD Dashboard',                 icon: Trophy,      testId: 'nav-dash-md' },
  { path: '/dashboard/finance',             key: 'dashboard/finance',          label: 'Finance Dashboard',            icon: Receipt,     testId: 'nav-dash-finance' },
  { path: '/dashboard/store',               key: 'dashboard/store',            label: 'Store Dashboard',              icon: Package,     testId: 'nav-dash-store' },
  { path: '/dashboard/service',             key: 'dashboard/service',          label: 'Service Dashboard',            icon: ListChecks,  testId: 'nav-dash-service' },
  { path: '/dashboard/digital-marketing',   key: 'dashboard/digital-marketing',label: 'Digital Marketing Dashboard',  icon: Megaphone,   testId: 'nav-dash-dm' },
  { path: '/dashboard/hr',                  key: 'dashboard/hr',               label: 'HR Dashboard',                 icon: Users,       testId: 'nav-dash-hr' },
  { path: '/leads-dashboard',               key: 'leads-dashboard',            label: 'Leads Dashboard',              icon: Funnel,      testId: 'nav-leads-dashboard' },
];

// B. MARKETING & LEADS
export const ALL_MARKETING_ITEMS = [
  { path: '/leads',                key: 'leads',                  label: 'Leads',               icon: Users,           testId: 'nav-leads' },
  { path: '/campaigns',            key: 'campaigns',              label: 'Campaigns',           icon: Megaphone,       testId: 'nav-campaigns' },
  { path: '/marketing-campaigns',  key: 'marketing-campaigns',    label: 'Campaign Module',     icon: Megaphone,       testId: 'nav-mktg-campaigns' },
  { path: '/admin/leads',          key: 'admin/leads',            label: 'Product Leads',       icon: Kanban,          testId: 'nav-product-leads' },
  { path: '/admin/enquiries',      key: 'admin/enquiries',        label: 'Enquiries',           icon: ChatTeardropText, testId: 'nav-enquiries' },
  { path: '/social-leads',         key: 'social-leads',           label: 'Social Leads',        icon: ShareNetwork,    testId: 'nav-social-leads' },
  { path: '/admin/dealers',        key: 'admin/dealers',          label: 'Dealer Management',   icon: Handshake,       testId: 'nav-admin-dealers' },
];

// C. DIGITAL MARKETING (Communication & Customer-Facing Templates)
export const ALL_DIGITAL_MARKETING_ITEMS = [
  { path: '/social-media',         key: 'social-media',           label: 'Social Media',          icon: Calendar,       testId: 'nav-social' },
  { path: '/social-hub',           key: 'social-hub',             label: 'Social Hub',            icon: ShareNetwork,   testId: 'nav-social-hub' },
  { path: '/email-marketing',      key: 'email-marketing',        label: 'Email',                 icon: Envelope,       testId: 'nav-email' },
  { path: '/whatsapp',             key: 'whatsapp',               label: 'WhatsApp',              icon: WhatsappLogo,   testId: 'nav-whatsapp' },
  { path: '/whatsapp/dashboard',   key: 'whatsapp/dashboard',     label: 'WhatsApp Dashboard',    icon: ChartBar,       testId: 'nav-whatsapp-dashboard' },
  { path: '/whatsapp-templates',   key: 'whatsapp-templates',     label: 'Communication Templates', icon: WhatsappLogo, testId: 'nav-wa-templates' },
  { path: '/analytics',            key: 'analytics',              label: 'Analytics',             icon: ChartLine,      testId: 'nav-analytics' },
  // Moved here from Admin per spec (customer-facing assets)
  { path: '/admin/public-links',   key: 'admin/public-links',     label: 'Public Links',          icon: ShareNetwork,   testId: 'nav-admin-public-links' },
  { path: '/admin/help-content',   key: 'admin/help-content',     label: 'Help Content',          icon: Question,       testId: 'nav-admin-help' },
];

// D. CRM & SALES (only modules that exist today)
export const ALL_CRM_ITEMS = [
  { path: '/crm',                  key: 'crm',                    label: 'CRM Overview',          icon: AddressBook,    testId: 'nav-crm-dashboard' },
  { path: '/crm/contacts',         key: 'crm/contacts',           label: 'Contacts',              icon: Users,          testId: 'nav-crm-contacts' },
  { path: '/crm/deals',            key: 'crm/deals',              label: 'Deals',                 icon: Handshake,      testId: 'nav-crm-deals' },
  { path: '/crm/tasks',            key: 'crm/tasks',              label: 'Tasks',                 icon: ListChecks,     testId: 'nav-crm-tasks' },
  { path: '/crm/activities',       key: 'crm/activities',         label: 'Activities',            icon: Pulse,          testId: 'nav-crm-activities' },
  { path: '/meetings',             key: 'meetings',               label: 'Meetings & Calls',      icon: Phone,          testId: 'nav-meetings' },
];

// E. PRODUCTS & CATALOGUE
export const ALL_PRODUCTS_ITEMS = [
  { path: '/admin/catalogue',                  key: 'admin/catalogue',        label: 'Product Management',    icon: Package,        testId: 'nav-admin-catalogue' },
  { path: '/admin/catalogue?tab=brands',       key: 'admin/brands',           label: 'Brand Master',          icon: Tag,            testId: 'nav-admin-brands' },
  { path: '/products',                         key: 'products',               label: 'Public Catalogue',      icon: Tag,            testId: 'nav-products' },
  { path: '/promotions',                       key: 'promotions',             label: 'Offers & Promotions',   icon: Sparkle,        testId: 'nav-promotions' },
];

// F. FINANCE / CFO — Billing landing + deep-linked sub-tab entries.
// Each `?tab=…` entry navigates to the same /billing route with a query that
// BillingCenter consumes to switch its active tab.
export const ALL_FINANCE_ITEMS = [
  { path: '/billing',                            key: 'billing',                 label: 'Billing',               icon: Receipt,    testId: 'nav-billing' },
  { path: '/billing?tab=estimations',            key: 'billing-quotations',      label: 'Quotations',            icon: FileText,   testId: 'nav-billing-quotations' },
  { path: '/billing?tab=proforma',               key: 'billing-proforma',        label: 'Proforma',              icon: FileText,   testId: 'nav-billing-proforma' },
  { path: '/billing?tab=invoices',               key: 'billing-invoices',        label: 'Invoices',              icon: Receipt,    testId: 'nav-billing-invoices' },
  { path: '/billing?tab=sales-orders',           key: 'billing-sales-orders',    label: 'Sales Orders',          icon: ListChecks, testId: 'nav-billing-sales-orders' },
  { path: '/billing?tab=payments',               key: 'billing-payments',        label: 'Payments / Collections',icon: CurrencyInr,testId: 'nav-billing-payments' },
  { path: '/billing?tab=aging',                  key: 'billing-aging',           label: 'Aging Report',          icon: ClockCounterClockwise, testId: 'nav-billing-aging' },
  { path: '/billing?tab=price-books',            key: 'billing-price-books',     label: 'Price Books',           icon: Tag,        testId: 'nav-billing-price-books' },
  { path: '/incentives',                         key: 'incentives',              label: 'Incentives',            icon: Gift,       testId: 'nav-incentives' },
  { path: '/incentive-srs',                      key: 'incentive-srs',           label: 'Incentive System',      icon: Trophy,     testId: 'nav-incentive-srs' },
];

// G. PURCHASE
export const ALL_PURCHASE_ITEMS = [
  { path: '/vendors',              key: 'vendors',                label: 'Vendors',               icon: Buildings,      testId: 'nav-vendors' },
  { path: '/purchase-orders',      key: 'purchase-orders',        label: 'Purchase Orders',       icon: ShoppingCart,   testId: 'nav-purchase-orders' },
];

// H. STORE & DELIVERY (only existing modules)
export const ALL_STORE_ITEMS = [
  { path: '/delivery-challans',    key: 'delivery-challans',      label: 'Delivery Challans',     icon: Truck,          testId: 'nav-delivery-challans' },
];

// I. OPERATIONS / SERVICE
export const ALL_OPERATIONS_ITEMS = [
  { path: '/service-tasks',        key: 'service-tasks',          label: 'Service & Installation',icon: Wrench,         testId: 'nav-service-tasks' },
  { path: '/amc-contracts',        key: 'amc-contracts',          label: 'AMC Contracts',         icon: ClockCounterClockwise, testId: 'nav-amc-contracts' },
];

// J. HR & TEAM
export const ALL_HR_ITEMS = [
  { path: '/user-management',      key: 'user-management',        label: 'User Management',       icon: UserPlus,       testId: 'nav-user-mgmt' },
  { path: '/admin/departments',    key: 'admin/departments',      label: 'Departments',           icon: Buildings,      testId: 'nav-admin-departments' },
  { path: '/admin/roles',          key: 'admin/roles',            label: 'Roles',                 icon: IdentificationCard, testId: 'nav-admin-roles' },
  { path: '/hr-staff',             key: 'hr-staff',               label: 'HR · Staff Messaging',  icon: IdentificationCard, testId: 'nav-hr-staff' },
  { path: '/team-performance',     key: 'team-performance',       label: 'Team',                  icon: Trophy,         testId: 'nav-team' },
];

// K. WORKSPACE
export const ALL_WORKSPACE_ITEMS = [
  { path: '/notes',                key: 'notes',                  label: 'Notes',                 icon: Note,           testId: 'nav-notes' },
  { path: '/alerts',               key: 'alerts',                 label: 'Alerts & Calendar',     icon: Bell,           testId: 'nav-alerts' },
  { path: '/help',                 key: 'help',                   label: 'Help & Support',        icon: Question,       testId: 'nav-help' },
];

// L. ADMIN SETTINGS (control-only; daily-use items moved out)
export const ALL_ADMIN_ITEMS = [
  { path: '/admin/settings',       key: 'admin/settings',         label: 'Company Settings',      icon: Gear,           testId: 'nav-admin-settings' },
];

// M. REPORTS — handled separately in Sidebar (Reports Hub + Export Reports)

// ============================================================================
// ROLE → SECTION VISIBILITY
// Keys here MUST match the keys in the ALL_*_ITEMS arrays above.
// Routes have NOT changed; only what each role can SEE in the sidebar has.
// ============================================================================

const ALL_DASHBOARD_KEYS = ALL_DASHBOARD_ITEMS.map(i => i.key);
const ALL_MARKETING_KEYS = ALL_MARKETING_ITEMS.map(i => i.key);
const ALL_DIGITAL_KEYS = ALL_DIGITAL_MARKETING_ITEMS.map(i => i.key);
const ALL_CRM_KEYS = ALL_CRM_ITEMS.map(i => i.key);
const ALL_PRODUCTS_KEYS = ALL_PRODUCTS_ITEMS.map(i => i.key);
const ALL_FINANCE_KEYS = ALL_FINANCE_ITEMS.map(i => i.key);
const ALL_PURCHASE_KEYS = ALL_PURCHASE_ITEMS.map(i => i.key);
const ALL_STORE_KEYS = ALL_STORE_ITEMS.map(i => i.key);
const ALL_OPERATIONS_KEYS = ALL_OPERATIONS_ITEMS.map(i => i.key);
const ALL_HR_KEYS = ALL_HR_ITEMS.map(i => i.key);
const ALL_WORKSPACE_KEYS = ALL_WORKSPACE_ITEMS.map(i => i.key);
const ALL_ADMIN_KEYS = ALL_ADMIN_ITEMS.map(i => i.key);

export const ROLE_ACCESS = {
  // Full access
  admin: {
    dashboards: ALL_DASHBOARD_KEYS,
    marketing: ALL_MARKETING_KEYS,
    digital_marketing: ALL_DIGITAL_KEYS,
    crm: ALL_CRM_KEYS,
    products: ALL_PRODUCTS_KEYS,
    finance: ALL_FINANCE_KEYS,
    purchase: ALL_PURCHASE_KEYS,
    store: ALL_STORE_KEYS,
    operations: ALL_OPERATIONS_KEYS,
    hr: ALL_HR_KEYS,
    workspace: ALL_WORKSPACE_KEYS,
    admin: ALL_ADMIN_KEYS,
    reports: true,
  },

  // MD — executive monitoring; broad VIEW across all dashboards + reports, no operational CRUD
  md: {
    dashboards: ALL_DASHBOARD_KEYS,
    marketing: ['leads', 'campaigns', 'marketing-campaigns', 'admin/leads'],
    digital_marketing: ['analytics', 'whatsapp/dashboard', 'social-hub'],
    crm: ALL_CRM_KEYS,
    products: ['products'],
    finance: ['billing', 'billing-quotations', 'billing-invoices', 'billing-aging', 'incentives'],
    purchase: ['purchase-orders'],
    store: ['delivery-challans'],
    operations: ['service-tasks', 'amc-contracts'],
    hr: ['team-performance', 'hr-staff'],
    workspace: ['notes', 'alerts', 'help'],
    admin: [],
    reports: true,
  },

  // HR — people management
  hr: {
    dashboards: ['dashboard', 'my-dashboard', 'dashboard/hr'],
    marketing: [],
    digital_marketing: [],
    crm: [],
    products: [],
    finance: [],
    purchase: [],
    store: [],
    operations: [],
    hr: ['user-management', 'admin/departments', 'hr-staff', 'team-performance'],
    workspace: ['notes', 'alerts', 'help'],
    admin: [],
    reports: true,
  },

  // CFO — finance-led; broad read across pipeline
  cfo: {
    dashboards: ['dashboard', 'my-dashboard', 'manager-dashboard', 'dashboard/md', 'dashboard/finance'],
    marketing: ['leads'],
    digital_marketing: ['analytics', 'whatsapp/dashboard'],
    crm: ['crm', 'crm/deals'],
    products: ['products'],
    finance: ALL_FINANCE_KEYS,
    purchase: ALL_PURCHASE_KEYS,
    store: ALL_STORE_KEYS,
    operations: ['amc-contracts'],
    hr: [],
    workspace: ['notes', 'alerts', 'help'],
    admin: [],
    reports: true,
  },

  // Marketing Head — campaigns/leads, plus customer-facing assets
  marketing_head: {
    dashboards: ['dashboard', 'my-dashboard', 'manager-dashboard', 'dashboard/digital-marketing', 'leads-dashboard'],
    marketing: ALL_MARKETING_KEYS,
    digital_marketing: ALL_DIGITAL_KEYS,
    crm: ALL_CRM_KEYS,
    products: ['admin/catalogue', 'products', 'promotions'],
    finance: ['billing', 'billing-quotations', 'incentives', 'incentive-srs'],
    purchase: [],
    store: [],
    operations: [],
    hr: [],
    workspace: ['notes', 'alerts', 'help'],
    admin: [],
    reports: true,
  },

  // Manager — broad operational access (≈ admin minus admin-settings)
  manager: {
    dashboards: ['dashboard', 'my-dashboard', 'manager-dashboard', 'bde-dashboard', 'dashboard/md', 'dashboard/finance', 'dashboard/store', 'dashboard/service', 'dashboard/digital-marketing', 'dashboard/hr', 'leads-dashboard'],
    marketing: ALL_MARKETING_KEYS,
    digital_marketing: ALL_DIGITAL_KEYS,
    crm: ALL_CRM_KEYS,
    products: ALL_PRODUCTS_KEYS,
    finance: ALL_FINANCE_KEYS,
    purchase: ALL_PURCHASE_KEYS,
    store: ALL_STORE_KEYS,
    operations: ALL_OPERATIONS_KEYS,
    hr: ['user-management', 'admin/departments', 'hr-staff', 'team-performance'],
    workspace: ALL_WORKSPACE_KEYS,
    admin: [],
    reports: true,
  },

  // BDM — team-lead view of CRM/sales pipeline
  bdm: {
    dashboards: ['dashboard', 'my-dashboard', 'manager-dashboard', 'bde-dashboard', 'leads-dashboard'],
    marketing: ['leads', 'campaigns', 'marketing-campaigns', 'admin/leads', 'social-leads'],
    digital_marketing: ['whatsapp', 'whatsapp/dashboard', 'social-hub'],
    crm: ALL_CRM_KEYS,
    products: ['products'],
    finance: ['billing', 'billing-quotations', 'billing-proforma', 'billing-invoices', 'billing-sales-orders', 'incentives', 'incentive-srs'],
    purchase: [],
    store: ['delivery-challans'],
    operations: ['service-tasks', 'amc-contracts'],
    hr: ['team-performance'],
    workspace: ALL_WORKSPACE_KEYS,
    admin: [],
    reports: true,
  },

  // BDE — minimal sidebar (locked spec from Phase A.2)
  bde: {
    dashboards: ['dashboard', 'my-dashboard', 'bde-dashboard', 'leads-dashboard'],
    marketing: ['leads', 'admin/leads'],
    digital_marketing: ['whatsapp', 'whatsapp/dashboard'],
    crm: ALL_CRM_KEYS,
    products: ['products'],
    finance: ['billing', 'billing-quotations', 'incentives', 'incentive-srs'],
    purchase: [],
    store: ['delivery-challans'],
    operations: ['service-tasks'],
    hr: [],
    workspace: ['notes', 'alerts', 'help'],
    admin: [],
    reports: false,
  },

  // Customer-facing portal
  customer: {
    dashboards: ['dashboard'],
    marketing: [],
    digital_marketing: [],
    crm: [],
    products: ['products'],
    finance: [],
    purchase: [],
    store: [],
    operations: [],
    hr: [],
    workspace: [],
    admin: [],
    reports: false,
  },
};

export const ROLE_BADGE_COLORS = {
  admin: 'bg-red-500/20 text-red-300',
  cfo: 'bg-amber-500/20 text-amber-300',
  marketing_head: 'bg-pink-500/20 text-pink-300',
  manager: 'bg-blue-500/20 text-blue-300',
  bdm: 'bg-green-500/20 text-green-300',
  bde: 'bg-yellow-500/20 text-yellow-300',
  customer: 'bg-slate-500/20 text-slate-300',
};

// ============================================================================
// PHASE B.5 — NAV ITEM PERMISSION REQUIREMENTS
// Each entry maps a nav item `key` to an array of permission keys.
// "Any-of" semantics: if user has ANY of the listed perms, the item is shown.
// An empty array means "no permission gate" (visible to all routed roles).
// Items not listed here default to "no gate" for forward-compat.
// ============================================================================
export const NAV_PERMISSION_MAP = {
  // Dashboards
  'dashboard': [],
  'my-dashboard': [],
  'manager-dashboard': ['dashboards.view_admin'],
  'bde-dashboard': ['dashboards.view_bde'],
  'dashboard/md': ['dashboards.view_md'],
  'dashboard/finance': ['dashboards.view_finance'],
  'dashboard/store': ['dashboards.view_store'],
  'dashboard/service': ['dashboards.view_service'],
  'dashboard/digital-marketing': ['dashboards.view_marketing'],
  'dashboard/hr': ['dashboards.view_hr'],
  'leads-dashboard': ['leads.view_all', 'leads.view_team', 'leads.view_assigned'],

  // Marketing & Leads
  'leads': ['leads.view_all', 'leads.view_team', 'leads.view_assigned'],
  'campaigns': ['campaigns.view'],
  'marketing-campaigns': ['campaigns.view', 'marketing.view_dashboard'],
  'admin/leads': ['leads.view_all', 'leads.view_team', 'leads.view_assigned'],
  'admin/enquiries': ['leads.view_all', 'leads.view_team'],
  'social-leads': ['leads.view_all', 'campaigns.view'],
  'admin/dealers': ['users.manage', 'users.view'],

  // Digital Marketing
  'social-media': ['social_media.view'],
  'social-hub': ['social_media.view'],
  'email-marketing': ['campaigns.view'],
  'whatsapp': ['leads.view_all', 'leads.view_team', 'leads.view_assigned'],
  'whatsapp/dashboard': ['dashboards.view_admin', 'marketing.view_dashboard', 'leads.view_all', 'leads.view_team', 'leads.view_assigned'],
  'whatsapp-templates': ['settings.manage'],
  'analytics': ['reports.view', 'marketing.view_dashboard'],
  'admin/public-links': ['settings.manage'],
  'admin/help-content': ['settings.manage'],

  // CRM & Sales
  'crm': ['contacts.view_all', 'contacts.view_assigned'],
  'crm/contacts': ['contacts.view_all', 'contacts.view_assigned'],
  'crm/deals': ['deals.view_all', 'deals.view_team', 'deals.view_assigned'],
  'crm/tasks': ['tasks.view_all', 'tasks.view_assigned'],
  'crm/activities': ['contacts.view_all', 'contacts.view_assigned'],
  'meetings': ['tasks.view_all', 'tasks.view_assigned'],

  // Products & Catalogue
  'admin/catalogue': ['catalogue.view_admin', 'products.create', 'products.edit'],
  'admin/brands': ['catalogue.view_admin', 'products.create', 'products.edit'],
  'products': ['products.view'],
  'promotions': ['campaigns.create', 'settings.manage'],

  // Finance
  'billing': ['billing.view', 'quotations.view_all', 'quotations.view_own', 'quotations.create'],
  'billing-quotations': ['quotations.view_all', 'quotations.view_own', 'quotations.create'],
  'billing-proforma': ['billing.view'],
  'billing-invoices': ['billing.view'],
  'billing-sales-orders': ['billing.view'],
  'billing-payments': ['billing.record_payment', 'billing.view'],
  'billing-aging': ['reports.financial', 'billing.view'],
  'billing-price-books': ['settings.manage', 'billing.view'],
  'incentives': ['incentives.view_own', 'incentives.view_all'],
  'incentive-srs': ['incentives.view_own', 'incentives.view_all'],

  // Purchase
  'vendors': ['purchase.manage_vendors', 'purchase.view'],
  'purchase-orders': ['purchase.view', 'purchase.create_po'],

  // Store
  'delivery-challans': ['store.view', 'store.create_dc'],

  // Operations / Service
  'service-tasks': ['service.view_all', 'service.view_assigned'],
  'amc-contracts': ['service.view_all', 'billing.view'],

  // HR & Team
  'user-management': ['users.view', 'users.manage'],
  'admin/departments': ['hr.manage', 'users.manage'],
  'admin/roles': ['roles.manage', 'users.manage'],
  'hr-staff': ['hr.view', 'hr.manage'],
  'team-performance': ['team.performance.view', 'reports.team_performance'],

  // Workspace
  'notes': ['notes.create'],
  'alerts': ['alerts.view'],
  'help': [],

  // Admin Settings
  'admin/settings': ['settings.manage', 'system.settings'],

  // Reports
  'reports-hub': ['reports.view'],
  'reports': ['reports.view', 'reports.export'],
};

// Helper used by the sidebar: does the user's perm_keys array satisfy this nav item?
// - Admin always sees everything.
// - Items not in the map (or with [] requires) are always visible.
// - Otherwise, "any-of" match against user.perm_keys.
export function navItemAllowedByPerms(itemKey, user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const required = NAV_PERMISSION_MAP[itemKey];
  if (!required || required.length === 0) return true;
  const perms = Array.isArray(user.perm_keys) ? user.perm_keys : null;
  if (!perms || perms.length === 0) return true;  // legacy session — fall back to ROLE_ACCESS only
  return required.some((k) => perms.includes(k));
}

// Backwards-compat exports for any code referencing old names.
// (We previously exported ALL_SALESFORCE_ITEMS — no consumer should remain after this PR.)
